import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { usdCompact, tokensCompact, shortDate } from '@/lib/format';
import type { UsageDayPoint, UsageDashboardDto } from '@cc/types';

// Stable colour map per model so the chart stays readable as new models appear.
function modelColor(model: string): string {
  if (model.includes('opus')) return '#a855f7';
  if (model.includes('sonnet')) return '#10b981';
  if (model.includes('haiku')) return '#60a5fa';
  return '#9ca3af';
}

const WINDOWS = [7, 30, 90] as const;

export function SettingsUsagePage() {
  const [windowDays, setWindowDays] = useState<(typeof WINDOWS)[number]>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.usage(windowDays),
    queryFn: () => api.getUsage(windowDays),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Settings
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Token usage</h1>
            <p className="text-sm text-muted-foreground">
              Anthropic spend. Cache-read tokens are billed at 10% of base, cache writes at 125%.
              Pricing in packages/ai/pricing.ts, dated 2026-05-22.
            </p>
          </div>
          <div className="inline-flex rounded-md border border-border bg-card p-0.5">
            {WINDOWS.map((d) => (
              <Button
                key={d}
                variant={d === windowDays ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setWindowDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">Failed to load usage data.</p>}

      {isLoading || !data ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-72" />
        </div>
      ) : (
        <UsageDashboard data={data} />
      )}
    </div>
  );
}

function UsageDashboard({ data }: { data: UsageDashboardDto }) {
  const { stacked, models } = useMemo(() => stackByDay(data.byDayByModel), [data.byDayByModel]);
  const empty = data.byDayByModel.length === 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Stat label={`Last ${data.windowDays} days`} value={usdCompact(data.totalCostUsd)} />
        <Stat label="Month to date" value={usdCompact(data.monthToDateUsd)} />
        <Stat
          label="Projected month"
          value={usdCompact(data.projectedMonthUsd)}
          sub="Naive linear projection from MTD."
        />
      </div>

      <Card>
        <CardContent className="p-5">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Daily cost by model
          </h2>
          {empty ? (
            <p className="py-12 text-center text-sm italic text-muted-foreground">
              No AI calls in this window yet. Triage, drafts, and briefs will populate this from
              Phase 2 onward.
            </p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stacked} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(d: string) => d.slice(5)}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => `$${v.toFixed(v < 1 ? 2 : 0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    formatter={(v: number) => usdCompact(v)}
                    labelFormatter={(d: string) => shortDate(d)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {models.map((m) => (
                    <Bar key={m} dataKey={m} stackId="cost" fill={modelColor(m)} radius={[2, 2, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              By prompt
            </h2>
            {data.byPrompt.length === 0 ? (
              <p className="py-6 text-center text-sm italic text-muted-foreground">No prompt usage yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-1 font-normal">Prompt</th>
                    <th className="py-1 font-normal">Calls</th>
                    <th className="py-1 font-normal">Cache</th>
                    <th className="py-1 text-right font-normal">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byPrompt.map((p) => (
                    <tr key={p.promptName} className="border-t border-border">
                      <td className="py-2 font-mono text-[12px]">{p.promptName}</td>
                      <td className="py-2 text-xs text-muted-foreground">{p.callCount}</td>
                      <td className="py-2 text-xs text-muted-foreground">
                        {(p.cacheHitRate * 100).toFixed(0)}%
                      </td>
                      <td className="py-2 text-right">{usdCompact(p.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Top 10 most expensive calls
            </h2>
            {data.topCalls.length === 0 ? (
              <p className="py-6 text-center text-sm italic text-muted-foreground">
                Nothing yet.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.topCalls.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-[12px]">{c.promptName}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {c.task} · {c.model.replace('claude-', '')} ·{' '}
                        {tokensCompact(c.inputTokens + c.outputTokens)} tok
                      </p>
                    </div>
                    <div className="text-right">
                      <p>{usdCompact(c.costUsd)}</p>
                      <p className="text-[11px] text-muted-foreground">{shortDate(c.createdAt)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold">{value}</p>
        {sub && <p className="mt-1 text-[11px] text-muted-foreground/80">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// Pivot rows from {date, model, cost} into {date, [modelA]: x, [modelB]: y, ...}
function stackByDay(rows: UsageDayPoint[]): { stacked: Record<string, unknown>[]; models: string[] } {
  const byDate = new Map<string, Record<string, number | string>>();
  const models = new Set<string>();
  for (const r of rows) {
    models.add(r.model);
    const cur = byDate.get(r.date) ?? { date: r.date };
    cur[r.model] = (Number(cur[r.model] ?? 0) + r.costUsd) as number;
    byDate.set(r.date, cur);
  }
  const stacked = Array.from(byDate.values()).sort((a, b) =>
    String(a.date).localeCompare(String(b.date)),
  );
  return { stacked, models: Array.from(models).sort() };
}

// Suppress unused
void Badge;
